// admin/src/scenes/reviews/index.jsx
// ─────────────────────────────────────────────────────────────────
// Admin page to manage:
//   - Book Reviews (hide/show/delete)
//   - Testimonials (approve/reject/delete)
// Accessed via admin sidebar
// ─────────────────────────────────────────────────────────────────
import {
  Box,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  CheckCircleOutline,
  Cancel,
  Delete,
  Star,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helper: star display ─────────────────────────────────────────
const StarRating = ({ rating }) => (
  <Box display="flex" gap="2px">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        sx={{
          fontSize: "16px",
          color: s <= rating ? "#f59e0b" : "#374151",
        }}
      />
    ))}
  </Box>
);

// ── Reviews Tab ──────────────────────────────────────────────────
const ReviewsTab = ({ colors }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/admin/reviews`, {
          headers,
        });
        setReviews(res.data.reviews || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVisibility = async (id, current) => {
    try {
      await axios.put(
        `${BASE_URL}/api/admin/reviews/${id}`,
        { isVisible: !current },
        { headers },
      );
      setReviews((prev) =>
        prev.map((r) => (r._id === id ? { ...r, isVisible: !current } : r)),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/admin/reviews/${id}`, { headers });
      setReviews((prev) => prev.filter((r) => r._id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;

  return (
    <TableContainer
      component={Paper}
      sx={{ backgroundColor: colors.primary[400] }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {[
              "Reviewer",
              "Book",
              "Rating",
              "Title",
              "Comment",
              "Verified",
              "Date",
              "Actions",
            ].map((h) => (
              <TableCell
                key={h}
                sx={{ color: colors.greenAccent[400], fontWeight: "bold" }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {reviews.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                sx={{ color: colors.grey[300], textAlign: "center", py: 4 }}
              >
                No reviews yet.
              </TableCell>
            </TableRow>
          ) : (
            reviews.map((r) => (
              <TableRow key={r._id} sx={{ opacity: r.isVisible ? 1 : 0.45 }}>
                <TableCell sx={{ color: colors.grey[100] }}>
                  <Typography fontWeight="bold" fontSize="13px">
                    {r.reviewerName}
                  </Typography>
                  <Typography fontSize="11px" color={colors.grey[400]}>
                    {r.reviewerEmail}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    color: colors.grey[100],
                    fontSize: "12px",
                    maxWidth: "120px",
                  }}
                >
                  {r.bookId?.title || "—"}
                </TableCell>
                <TableCell>
                  <StarRating rating={r.rating} />
                </TableCell>
                <TableCell sx={{ color: colors.grey[100], fontSize: "12px" }}>
                  {r.title || "—"}
                </TableCell>
                <TableCell
                  sx={{
                    color: colors.grey[300],
                    fontSize: "12px",
                    maxWidth: "200px",
                  }}
                >
                  <Typography noWrap fontSize="12px">
                    {r.comment || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={r.verifiedPurchase ? "Verified" : "Unverified"}
                    size="small"
                    sx={{
                      backgroundColor: r.verifiedPurchase
                        ? "#166534"
                        : "#374151",
                      color: "#fff",
                      fontSize: "11px",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: colors.grey[400], fontSize: "11px" }}>
                  {new Date(r.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap="4px">
                    <Tooltip
                      title={r.isVisible ? "Hide review" : "Show review"}
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleVisibility(r._id, r.isVisible)}
                        sx={{
                          color: r.isVisible
                            ? colors.greenAccent[400]
                            : colors.grey[500],
                        }}
                      >
                        {r.isVisible ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => deleteReview(r._id)}
                        sx={{ color: "#ef4444" }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Testimonials Tab ─────────────────────────────────────────────
const TestimonialsTab = ({ colors }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending|approved|rejected

  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/api/admin/testimonials?status=${filter}`,
          { headers },
        );
        setTestimonials(res.data.testimonials || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await axios.put(
        `${BASE_URL}/api/admin/testimonials/${id}`,
        { status },
        { headers },
      );
      setTestimonials((prev) => prev.filter((t) => t._id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/admin/testimonials/${id}`, {
        headers,
      });
      setTestimonials((prev) => prev.filter((t) => t._id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const STATUS_COLORS = {
    pending: { bg: "#92400e", text: "#fef3c7" },
    approved: { bg: "#166534", text: "#dcfce7" },
    rejected: { bg: "#991b1b", text: "#fee2e2" },
  };

  return (
    <Box>
      {/* Filter tabs */}
      <Box display="flex" gap="8px" mb="16px">
        {["pending", "approved", "rejected"].map((s) => (
          <Chip
            key={s}
            label={s.charAt(0).toUpperCase() + s.slice(1)}
            onClick={() => setFilter(s)}
            sx={{
              backgroundColor:
                filter === s ? colors.greenAccent[700] : colors.primary[400],
              color: colors.grey[100],
              cursor: "pointer",
              fontWeight: filter === s ? "bold" : "normal",
            }}
          />
        ))}
      </Box>

      {loading ? (
        <CircularProgress sx={{ m: 4 }} />
      ) : (
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: colors.primary[400] }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "Name",
                  "Location / Role",
                  "Message",
                  "Rating",
                  "Status",
                  "Date",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ color: colors.greenAccent[400], fontWeight: "bold" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {testimonials.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ color: colors.grey[300], textAlign: "center", py: 4 }}
                  >
                    No {filter} testimonials.
                  </TableCell>
                </TableRow>
              ) : (
                testimonials.map((t) => (
                  <TableRow key={t._id}>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      <Typography fontWeight="bold" fontSize="13px">
                        {t.name}
                      </Typography>
                      <Typography fontSize="11px" color={colors.grey[400]}>
                        {t.email}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{ color: colors.grey[300], fontSize: "12px" }}
                    >
                      {[t.role, t.location].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: "240px" }}>
                      <Typography
                        fontSize="12px"
                        color={colors.grey[200]}
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        "{t.message}"
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StarRating rating={t.rating || 5} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.status}
                        size="small"
                        sx={{
                          backgroundColor:
                            STATUS_COLORS[t.status]?.bg || "#374151",
                          color: STATUS_COLORS[t.status]?.text || "#fff",
                          fontSize: "11px",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ color: colors.grey[400], fontSize: "11px" }}
                    >
                      {new Date(t.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap="4px">
                        {t.status !== "approved" && (
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              onClick={() => updateStatus(t._id, "approved")}
                              sx={{ color: "#22c55e" }}
                            >
                              <CheckCircleOutline />
                            </IconButton>
                          </Tooltip>
                        )}
                        {t.status !== "rejected" && (
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              onClick={() => updateStatus(t._id, "rejected")}
                              sx={{ color: "#f59e0b" }}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => deleteTestimonial(t._id)}
                            sx={{ color: "#ef4444" }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── Main Page ────────────────────────────────────────────────────
const ReviewsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tab, setTab] = useState(0);

  return (
    <Box m="20px">
      <Header
        title="REVIEWS & TESTIMONIALS"
        subtitle="Manage book reviews and homepage testimonials"
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: "20px",
          "& .MuiTab-root": { color: colors.grey[300] },
          "& .Mui-selected": { color: `${colors.greenAccent[400]} !important` },
          "& .MuiTabs-indicator": { backgroundColor: colors.greenAccent[400] },
        }}
      >
        <Tab label="⭐ Book Reviews" />
        <Tab label="💬 Testimonials" />
      </Tabs>

      {tab === 0 && <ReviewsTab colors={colors} />}
      {tab === 1 && <TestimonialsTab colors={colors} />}
    </Box>
  );
};

export default ReviewsPage;
